import type { ConsoleToolHealthRow } from '@plato/shared';
import { DataTable } from '../../components/data-table.tsx';
import { formatCount, formatDate } from '../../components/format.ts';
import { Loaded } from '../../components/page.tsx';
import { useTenantQuery } from '../../data/console-context.tsx';
import type { ConsoleData, TenantSummary } from '../../data/console-data.ts';

const FAILING_SHARE = 0.05;

const loadHealth = (data: ConsoleData, tenant: TenantSummary) => data.getToolHealth(tenant.id);

export function ToolHealth() {
  const health = useTenantQuery(loadHealth);
  return (
    <Loaded query={health}>
      {(value) => {
        const rows = [...value.data].sort(
          (a, b) => b.day.localeCompare(a.day) || a.tool_name.localeCompare(b.tool_name),
        );
        const failing = new Set(rows.filter(isFailing).map((row) => row.tool_name));
        return (
          <>
            <p className="muted">
              {formatDate(value.from)} to {formatDate(value.to)}, by day (UTC).
            </p>
            {failing.size > 0 && (
              <p className="notice notice-danger" role="status">
                Failing more than {FAILING_SHARE * 100}% of calls on some day:{' '}
                {[...failing].join(', ')}.
              </p>
            )}
            <DataTable
              caption="Tool health"
              columns={[
                { header: 'Day', cell: (row) => row.day },
                { header: 'Tool', cell: (row) => <code>{row.tool_name}</code> },
                { header: 'Source', cell: (row) => row.mcp_server ?? row.tool_kind },
                { header: 'Calls', cell: (row) => formatCount(row.calls) },
                {
                  header: 'Failures',
                  cell: (row) =>
                    isFailing(row) ? (
                      <span className="chip chip-danger">
                        {formatCount(row.failures)} ({percent(row)})
                      </span>
                    ) : (
                      formatCount(row.failures)
                    ),
                },
                { header: 'Median', cell: (row) => `${formatCount(row.p50_ms)} ms` },
                { header: '95th percentile', cell: (row) => `${formatCount(row.p95_ms)} ms` },
              ]}
              rows={rows.map((row) => ({
                ...row,
                id: `${row.day}/${row.mcp_server}/${row.tool_name}`,
              }))}
            />
            {rows.length === 0 && <p className="muted">No tool calls in this period.</p>}
          </>
        );
      }}
    </Loaded>
  );
}

const isFailing = (row: ConsoleToolHealthRow) =>
  row.calls > 0 && row.failures / row.calls > FAILING_SHARE;

const percent = (row: ConsoleToolHealthRow) => `${Math.round((row.failures / row.calls) * 100)}%`;
